import { JSX } from "react";
import { Modal, Table, Button, Tooltip } from "antd";
import {
  StatusSignedIcon,
  StatusDeclinedIcon,
  StatusNewIcon,
  StatusPendingIcon,
  StatusPendingGsnIcon,
  CommentIcon,
} from "@/shared/assets/svg/icons";
import { formatDate, formatMoscowTime, formatMoscowDatetime } from "@/shared/utils";
import type { components } from "@entities/journal";
import { UserBubble } from "@shared/ui";
import { useGetItemsVersion } from "../../model/use-get-items-version";

type Item =
  components["schemas"]["SectionOneListItemVersionGetForListResponse"];

type Props = {
  open: boolean;
  onCancel: () => void;
  section1ListId?: string;
  itemNoVersionId?: string;
};

export const JournalVersionModal = ({
  open,
  onCancel,
  section1ListId,
  itemNoVersionId,
}: Props) => {
  const { data, isLoading } = useGetItemsVersion(
    section1ListId,
    itemNoVersionId,
  );
  const versions = data?.section1List || [];

  const statusIcons: Record<string, { icon: JSX.Element; tooltip: string }> = {
    NEW: {
      icon: <StatusNewIcon />,
      tooltip: "Новый",
    },
    AWAITING_SIGNATURE: {
      icon: <StatusPendingIcon />,
      tooltip: "Ожидает подписания",
    },
    REJECTED: {
      icon: <StatusDeclinedIcon />,
      tooltip: "Отклонено",
    },
    SIGNED: {
      icon: <StatusSignedIcon />,
      tooltip: "Подписано",
    },
    SENT_TO_GSN: {
      icon: <StatusPendingGsnIcon />,
      tooltip: "Отправлено в ГСН",
    },
  };

  const columns = [
    {
      title:
        "Полное и (или) сокращенное наименование или фамилия, имя отчество (последнее - при наличии) лица, осуществляющего строительство",
      dataIndex: "signer",
      render: (_: any, row: Item) => {
        const date = row?.updateDate;

        const fio = `${row.userCompany?.user?.surname} ${row.userCompany?.user?.firstName?.[0] ?? ""}.${row.userCompany?.user?.middleName?.[0] ?? ""}.`;

        return (
          <div>
            <div className="journal-version-modal-user-row">
              <UserBubble userId={row.userCompany?.user?.userId || ""}>
                <div className="journal-version-modal-user-link">
                  {fio}
                </div>
              </UserBubble>
              <span className="journal-version-modal-date">
                {date ? formatDate(date) || "-" : "-"}
              </span>
              {date && (
                <span className="journal-version-modal-time">
                  {formatMoscowTime(date)} МСК
                </span>
              )}
            </div>
            <div className="journal-version-modal-company">{row?.company?.fullName}</div>
          </div>
        );
      },
    },
    {
      title:
        "Фамилия, инициалы, должность (при наличии) лица, входящего в список инженерно-технического персонала",
      dataIndex: "person",
      render: (_: any, row: Item) => {
        return (
          <div>
            <UserBubble userId={row?.userCompany?.user?.userId || ""}>
              <div className="journal-version-modal-user-link journal-version-modal-user-link--md">
                {row?.userCompany?.user?.surname}{" "}
                {row?.userCompany?.user?.firstName?.[0] ?? ""}.
                {row?.userCompany?.user?.middleName?.[0] ?? ""}.
              </div>
            </UserBubble>
            <div>{row?.userCompany?.user?.position}</div>
          </div>
        );
      },
    },
    {
      title:
        "Дата начала работ на объекте капитального строительства с указанием вида работ",
      dataIndex: "userCompany",
      render: (_: any, row: Item) => {
        return (
          <div className="journal-version-modal-column">
            <div className="journal-version-modal-cell-text">
              {formatDate(row?.dateStart) || "-"}
            </div>
            <div>{row?.workType}</div>
          </div>
        );
      },
    },
    {
      title: "Дата окончания работ на объекте капитального строительства",
      dataIndex: "dateEnd",
      render: (_: any, row: Item) => {
        return (
          <div className="journal-version-modal-cell-text">
            {formatDate(row?.dateEnd) || "-"}
          </div>
        );
      },
    },
    {
      title: "Уполномоченный представитель лица, осуществляющего строительство",
      dataIndex: "signer",
      render: (_: any, row: Item) => {
        const signer = row?.signer;
        if (!signer) return "-";

        const fio = `${signer.surname} ${signer.firstName?.[0] ?? ""}.${signer.middleName?.[0] ?? ""}.`;

        const status = row.status as string;
        const statusInfo = statusIcons[status];

        return (
          <div className="journal-version-modal-signer-cell">
            <div className="journal-version-modal-version-badge">
              Версия {row.versionNumber}
            </div>

            <div className="journal-version-modal-rep-row">
              <UserBubble userId={signer.userId || ""}>
                <Button type="link" style={{ padding: 0 }}>
                  {fio}
                </Button>
              </UserBubble>

              {statusInfo ? (
                <Tooltip
                  title={
                    statusInfo.tooltip +
                    (row.updateDate
                      ? ` ${formatMoscowDatetime(row.updateDate)}`
                      : "")
                  }
                >
                  {statusInfo.icon}
                </Tooltip>
              ) : null}

              {row.actionDetails && (
                <Tooltip title={row.actionDetails}>
                  <CommentIcon />
                </Tooltip>
              )}
            </div>

            <div>{signer.position}</div>
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={
        <div className="journal-version-modal-footer">
          <Button type="primary" onClick={onCancel}>
            Закрыть
          </Button>
        </div>
      }
      width={1200}
      title={<div className="journal-version-modal-title">Версии записи</div>}
    >
      <Table
        columns={columns}
        dataSource={versions}
        rowKey="itemNoVersionId"
        pagination={false}
        loading={isLoading}
      />
    </Modal>
  );
};
